import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { gsap } from 'gsap';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

export default function Features() {
  useEffect(() => {
    document.title = "Outlne Features | Generate Videos for any Project";
  }, []);

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const lastGridChangeTime = useRef(0);
  const containerRef = useRef(null);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);
  const threeMountRef = useRef(null);
  const featuresContainerRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  const features = [
    {
      title: "Generative Interior Design",
      description: "Customize any room, upload a photo (.HEIC, .JPEG, .PNG) and place hyper-realistically generated furniture, decor, and architectural elements to match your style preferences and layout requirements."
    },
    {
      title: "Precision 3D Perspective Matching",
      description: "Automatically detect room perspective and place 3D models with perfect alignment for realistic interior visualizations."
    },
    {
      title: "3D Model Import & Manipulation",
      description: "Import custom 3D furniture models, adjust angles, scale, and placement with intuitive controls for precise design execution."
    },
    {
      title: "Style-Based Generative Recommendations",
      description: "Get intelligent suggestions for furniture arrangements, color palettes, and decor based on your preferred design aesthetic."
    },
    {
      title: "Portfolio-Quality Export Options",
      description: "Export HD to 8K resolution images and videos perfect for social media content and client presentations. Includes suggested cinematic angles for professional-looking results."
    },
    {
      title: "Collaborative Design Workspace",
      description: "Work with clients or team members in real-time, sharing designs, collecting feedback, and making collaborative changes."
    },
    {
      title: "Brand-Commercial Usage Rights",
      description: "Use generated content professionally with commercial licensing for portfolios, client work, and business presentations."
    }

  ];

  // Check if terms were already accepted
  useEffect(() => {
    const accepted = localStorage.getItem('outlne_terms_accepted');
    if (accepted === 'true') {
      setTermsAccepted(true);
    }
  }, []);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded && termsAccepted) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded, termsAccepted]);

  // Grid animation - only run after page has loaded and terms accepted
  useEffect(() => {
    if (!pageLoaded || !termsAccepted) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded, termsAccepted]);

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    localStorage.setItem('outlne_terms_accepted', 'true');
  };

  const handleDeclineTerms = () => {
    navigate('/');
  };

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Optimized Background Slideshow - Fades in after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container - Fades out after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Terms Agreement Overlay */}
      {!termsAccepted && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(34, 34, 34, 0.95)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: 'rgba(34, 34, 34, 0.95)',
            border: '2px solid white',
            borderRadius: '30px',
            padding: isMobile ? '25px' : '40px',
            maxWidth: isMobile ? '90vw' : '500px',
            textAlign: 'center',
            color: 'white',
            fontFamily: 'avenir-lt-pro, sans-serif',
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.4rem' : '1.8rem',
              marginBottom: '1rem',
              fontWeight: '500',
            }}>
              Before you Explore
            </h2>
            <p style={{
              fontSize: isMobile ? '0.9rem' : '1rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              color: '#ccc',
            }}>
              Continuing means you agree to our<br></br><a href="/terms" style={{ color: '#fff', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Service</a>
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <button
                onClick={handleDeclineTerms}
                style={{
                  padding: '12px 30px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '25px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontWeight: '500',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: isMobile ? '100%' : '120px',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Decline
              </button>
              <button
                onClick={handleAcceptTerms}
                style={{
                  padding: '12px 30px',
                  backgroundColor: 'white',
                  color: '#000',
                  border: 'none',
                  borderRadius: '25px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontWeight: '500',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  minWidth: isMobile ? '100%' : '120px',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Agree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header */}
      {!isMobile && termsAccepted && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header with Blur Background */}
      {isMobile && termsAccepted && (
        <>
          {/* Blur Background Bar */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          {/* Mobile Header Content - Left Side */}
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          {/* Mobile Header Content - Right Side */}
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content Container - Scrollable with Footer */}
      {termsAccepted && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 5,
          overflowY: 'auto',
          overflowX: 'hidden',
          opacity: pageLoaded ? 1 : 0,
          transition: 'opacity 1s ease',
          overscrollBehavior: 'none'
        }}>
          
          {/* Content Wrapper */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            minHeight: '100vh',
            paddingTop: isMobile ? '130px' : '150px',
            paddingBottom: '0',
            position: 'relative',
            zIndex: 5,
          }}>
            
            {/* Scrollable Modal Container */}
            <div style={{
              width: isMobile ? '85vw' : isTablet ? '60vw' : '800px',
              height: isMobile ? 'auto' : isTablet ? 'auto' : 'auto',
              minHeight: isMobile ? 'auto' : '500px',
              border: '2px solid white',
              borderRadius: '40px',
              overflow: 'hidden',
              padding: isMobile ? '15px' : '20px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(34, 34, 34, 0.95)',
              marginBottom: '3rem',
            }}>
              
              {/* Features Header inside the screen */}
              <div style={{
                textAlign: 'center',
                marginBottom: isMobile ? '1.5rem' : '2rem',
                padding: isMobile ? '0 10px' : '0 20px',
                flexShrink: 0
              }}>
                <h1 style={{
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem',
                  fontWeight: '500',
                  color: 'white',
                  marginBottom: '0.5rem',
                  lineHeight: '1.2',
                  letterSpacing: '-0.5px'
                }}>
                  Modern Interior Designing
                </h1>
                <p style={{
                  color: '#ccc',
                  fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                  lineHeight: '1.4',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  margin: 0
                }}>
                  Our innovative features utilize hyper-realistic generative visualization & rendering techniques.<br />Built for any project.
                </p>
              </div>

              {/* Scrollable Features List */}
              <div ref={featuresContainerRef} style={{
                flex: 1,
                overflowY: 'auto',
                padding: isMobile ? '0 5px' : '0 10px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.3) transparent',
                borderRadius: '12px',
                maxHeight: isMobile ? '400px' : '500px',
              }}>
                <style>
                  {`
                    .features-list-container::-webkit-scrollbar {
                      width: 6px;
                    }
                    .features-list-container::-webkit-scrollbar-track {
                      background: transparent;
                    }
                    .features-list-container::-webkit-scrollbar-thumb {
                      background: rgba(255,255,255,0.3);
                      border-radius: 3px;
                    }
                    .features-list-container::-webkit-scrollbar-thumb:hover {
                      background: rgba(255,255,255,0.5);
                    }
                  `}
                </style>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem',
                  padding: isMobile ? '15px 5px 20px 5px' : '20px 10px 20px 10px',
                }}>
                  {features.map((feature, index) => (
                    <div key={index}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.25rem',
                        transition: 'all 0.3s ease',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        cursor: 'pointer',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <h3 style={{
                        fontFamily: 'avenir-lt-pro, sans-serif',
                        fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
                        fontWeight: '500',
                        color: 'white',
                        marginBottom: '0.5rem',
                        lineHeight: '1.3',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                      }}>
                        {feature.title}
                      </h3>
                      <p style={{
                        color: '#ccc',
                        fontSize: isMobile ? '0.85rem' : isTablet ? '0.9rem' : '0.95rem',
                        lineHeight: '1.5',
                        margin: 0,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                      }}>
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button inside modal */}
              <div style={{
                textAlign: 'center',
                paddingTop: isMobile ? '1rem' : '1.5rem',
                marginTop: 'auto',
                flexShrink: 0,
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <button
                  onClick={() => navigate('/pricing')}
                  style={{
                    padding: isMobile ? '10px 20px' : '12px 24px',
                    backgroundColor: 'white',
                    color: '#000',
                    border: 'none',
                    borderRadius: '25px',
                    fontFamily: 'avenir-lt-pro, sans-serif',
                    fontWeight: '500',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  View Pricing
                </button>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              width: '100%',
              position: 'relative',
              zIndex: 5,
              backgroundColor: '#1a1a1a',
              marginTop: 'auto',
            }}>
              <Footer />
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 0.2; }
          }
          
          html {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
          }
          
          body {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          
          #root {
            height: 100%;
            width: 100%;
            overflow: hidden;
            position: fixed;
          }
          
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
          
          /* Prevent any gradient masks on cards */
          div[style*="mask-image"] {
            pointer-events: none;
          }
        `}
      </style>
    </>
  );
}